import React, { useMemo } from 'react';
import type { RatingResultsPayload } from 'coop-adventure-engine';
import PortraitImage from './PortraitImage';
import {
  formatRatingAverage,
  RATING_STAR_COUNT,
  RATING_STAR_PATH,
  starFillFraction
} from './ratingStars';

interface RatingResultsProps {
  variableName: string;
  results?: RatingResultsPayload | undefined;
  className?: string | undefined;
}

const ReadOnlyStars: React.FC<{
  value: number;
  size: 'large' | 'small';
  labelledBy?: string;
}> = ({ value, size, labelledBy }) => {
  const stars = Array.from({ length: RATING_STAR_COUNT }, (_, index) => {
    const starIndex = index + 1;
    const fill = starFillFraction(value, starIndex);
    const pct = Math.round(fill * 1000) / 10;
    return (
      <span
        key={starIndex}
        className={
          size === 'small'
            ? 'rich-text-rating-results__mini-star'
            : 'rich-text-rating-results__star'
        }
        data-star-index={starIndex}
        data-fill={String(fill)}
        aria-hidden="true"
      >
        <svg
          className="rich-text-rating-results__star-empty"
          viewBox="0 0 24 24"
          focusable="false"
        >
          <path d={RATING_STAR_PATH} />
        </svg>
        {pct > 0 && (
          <span
            className="rich-text-rating-results__star-fill"
            style={{ width: `${pct}%` }}
          >
            <svg viewBox="0 0 24 24" focusable="false">
              <path d={RATING_STAR_PATH} />
            </svg>
          </span>
        )}
      </span>
    );
  });

  return (
    <div
      className={
        size === 'small'
          ? 'rich-text-rating-results__mini-stars'
          : 'rich-text-rating-results__stars'
      }
      {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
    >
      {stars}
    </div>
  );
};

const RatingResults: React.FC<RatingResultsProps> = ({
  variableName,
  results,
  className
}) => {
  const count = results?.count ?? 0;
  const average = results?.average ?? 0;
  const votes = results?.votes ?? [];
  const averageLabel = formatRatingAverage(average, count);
  const displayAverage = count === 0 ? 0 : Number(average.toFixed(1));
  const averageId = `rating-results-avg-${variableName}`;

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-rating-results'];
    if (className) classes.push(className);
    return classes.join(' ');
  }, [className]);

  return (
    <div
      className={rootClassName}
      data-variable={variableName}
      data-testid={`rating-results-${variableName}`}
      data-average={averageLabel}
      data-count={String(count)}
    >
      <div className="rich-text-rating-results__summary">
        <ReadOnlyStars value={displayAverage} size="large" labelledBy={averageId} />
        <div className="rich-text-rating-results__average" id={averageId}>
          <span className="rich-text-rating-results__average-value">{averageLabel}</span>
          <span className="rich-text-rating-results__count">
            {count === 1 ? '1 vote' : `${count} votes`}
          </span>
        </div>
      </div>

      {votes.length > 0 && (
        <ul className="rich-text-rating-results__voters" aria-label="Votes">
          {votes.map(vote => {
            const label = vote.displayName || vote.character;
            return (
            <li
              key={vote.character}
              className="rich-text-rating-results__voter"
              data-character={vote.character}
            >
              <PortraitImage
                portraitId={vote.portrait || 'generic'}
                variant="thumbnail"
                alt={label}
              />
              <span className="rich-text-rating-results__voter-name">{label}</span>
              <ReadOnlyStars value={Number(vote.value) || 0} size="small" />
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RatingResults;
